import { useState } from "react";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, errorText, SERVICES } from "@/lib/api";
import { Field, todayStr } from "@/components/site/Field";

const DURATIONS = [{ id: "60", label: "1 hour", price: "$35" }, { id: "30", label: "30 minutes", price: "$25" }];
const EMPTY = { name: "", email: "", phone: "", preferred_date: "", message: "" };

const Sent = ({ onAgain }) => (
  <div className="text-center py-6 rise" data-testid="booking-success">
    <CheckCircle2 className="mx-auto text-gold" size={40} strokeWidth={1.3} />
    <h3 className="font-display gold-text text-3xl mt-5">Request received</h3>
    <p className="mt-4 text-[#f7f1e1]/80">Thank you, beloved soul. I will reach out by email or phone to confirm the time of our session.</p>
    <button className="btn-ghost mt-8" onClick={onAgain} data-testid="booking-again-btn">Send another request</button>
  </div>
);

export const BookingForm = ({ service, setService, duration, setDuration }) => {
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!service) return toast.error("Please choose a service");
    setLoading(true);
    try {
      await api.post("/bookings", { ...form, phone: form.phone || null, message: form.message || null, service, duration });
      setSent(true);
      setForm(EMPTY);
    } catch (err) {
      toast.error(errorText(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="request" data-testid="booking-section" className="px-6 md:px-16 py-24 md:py-32">
      <div className="max-w-3xl mx-auto glass rounded-[2rem] p-8 md:p-14">
        {sent ? <Sent onAgain={() => setSent(false)} /> : (
          <>
            <p className="eyebrow">Begin your healing</p>
            <h2 className="font-display gold-text text-3xl md:text-5xl mt-3">Request a Session</h2>
            <p className="mt-4 text-[#f7f1e1]/75">Choose your session and share a little about what you need. I will reach out to confirm the time and how to pay.</p>
            <form onSubmit={submit} className="space-y-6 mt-10" data-testid="booking-form">
              <div>
                <span className="field-label">Session length</span>
                <div className="grid grid-cols-2 gap-4 mt-2">
                  {DURATIONS.map((d) => (
                    <button type="button" key={d.id} className="choice text-left" data-active={duration === d.id} onClick={() => setDuration(d.id)} data-testid={`booking-duration-${d.id}`}>
                      <span className="block font-cinzel text-[#f1d57a]">{d.label}</span>
                      <span className="block font-display gold-text text-2xl mt-1">{d.price}</span>
                    </button>
                  ))}
                </div>
              </div>
              <div className="grid md:grid-cols-2 gap-6">
                <Field label="Name"><input required className="field" placeholder="Your name" value={form.name} onChange={set("name")} data-testid="booking-name-input" /></Field>
                <Field label="Email"><input required type="email" className="field" placeholder="you@email.com" value={form.email} onChange={set("email")} data-testid="booking-email-input" /></Field>
                <Field label="Phone (optional)"><input className="field" placeholder="Your phone" value={form.phone} onChange={set("phone")} data-testid="booking-phone-input" /></Field>
                <Field label="Preferred date"><input required type="date" min={todayStr()} className="field" value={form.preferred_date} onChange={set("preferred_date")} data-testid="booking-date-input" /></Field>
              </div>
              <Field label="Service">
                <select required className="field" value={service} onChange={(e) => setService(e.target.value)} data-testid="booking-service-select">
                  <option value="">Choose a service</option>
                  {SERVICES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </Field>
              <Field label="Message"><textarea rows={5} className="field" placeholder="Share what is on your heart..." value={form.message} onChange={set("message")} data-testid="booking-message-input" /></Field>
              <button type="submit" disabled={loading} className="btn-gold" data-testid="booking-submit-btn">{loading ? "Sending..." : "Send Request"}</button>
            </form>
          </>
        )}
      </div>
    </section>
  );
};
